import React, { useEffect } from "react";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";
import BusinessOutlined from "@mui/icons-material/BusinessOutlined";
import PhoneOutlined from "@mui/icons-material/PhoneOutlined";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LanguageOutlined from "@mui/icons-material/LanguageOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import { getCustomer, getMachine, PublicState } from "../../publicSlice";
import { dt } from "../../../shared/themes/redesignTokens";
import useTranslation from "../../../shared/hooks/useTranslation";
import KioskBackButton from "../kiosk/KioskBackButton";

const CompanyDetailsPage: React.FC = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("public");

  const { machine, customer, loading } = useAppSelector<PublicState>(
    (state: any) => state.public
  );

  useEffect(() => {
    if (id && !machine) {
      dispatch(getMachine(id));
    }
  }, [id, machine, dispatch]);

  useEffect(() => {
    if (machine?.customerId) {
      dispatch(getCustomer(machine.customerId));
    }
  }, [machine?.customerId, dispatch]);

  if (!customer && loading) {
    return (
      <Box sx={{ px: 2, py: 4, textAlign: "center", color: dt.textMuted, fontSize: "14px" }}>
        {translate("loading")}
      </Box>
    );
  }

  if (!customer) {
    return null;
  }

  const addressParts = [
    customer.address?.addressLine,
    customer.address?.zipCode,
    customer.address?.city,
    customer.address?.countryName,
  ].filter(Boolean);

  const addressDisplay = addressParts.join(", ");

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        px: 2,
        pb: "80px",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          mb: 2,
        }}
      >
        <BusinessOutlined sx={{ fontSize: "24px", color: dt.blueHover }} />
        <Box
          sx={{
            fontSize: "18px",
            fontWeight: 700,
            color: dt.textPrimary,
          }}
        >
          {translate("kioskCompanyDetails")}
        </Box>
      </Box>

      {/* Company card */}
      <Box
        sx={{
          backgroundColor: dt.cardBg,
          borderRadius: dt.radius,
          border: `1px solid ${dt.cardBorder}`,
          boxShadow: dt.cardShadow,
          overflow: "hidden",
        }}
      >
        {/* Logo + Name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
            padding: "20px",
            borderBottom: `1px solid ${dt.cardBorder}`,
          }}
        >
          {/* Company logo */}
          <Box
            sx={{
              width: "64px",
              height: "64px",
              flexShrink: 0,
              borderRadius: dt.radiusSm,
              border: `1px solid ${dt.cardBorder}`,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#F8FAFC",
            }}
          >
            {customer.logo?.content ? (
              <img
                src={customer.logo.content}
                alt={customer.name}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <BusinessOutlined
                sx={{ fontSize: "28px", color: dt.textMuted, opacity: 0.5 }}
              />
            )}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                fontSize: "18px",
                fontWeight: 700,
                color: dt.textPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {customer.name}
            </Box>
            {customer.address?.countryName && (
              <Box
                sx={{
                  fontSize: "13px",
                  fontWeight: 400,
                  color: dt.textSecondary,
                  mt: "2px",
                }}
              >
                {customer.address.countryName}
              </Box>
            )}
          </Box>
        </Box>

        {/* Contact details */}
        <Box sx={{ padding: "16px", display: "flex", flexDirection: "column", gap: "14px" }}>
          {/* Address */}
          {addressDisplay && (
            <ContactRow
              icon={<LocationOnOutlined />}
              label={translate("companyAddress")}
              value={addressDisplay}
            />
          )}

          {/* Phone */}
          {customer.phone && (
            <ContactRow
              icon={<PhoneOutlined />}
              label={translate("companyPhone")}
              value={customer.phone}
              href={`tel:${customer.phone}`}
            />
          )}

          {/* Email */}
          {customer.email && (
            <ContactRow
              icon={<EmailOutlined />}
              label={translate("companyEmail")}
              value={customer.email}
              href={`mailto:${customer.email}`}
            />
          )}

          {/* Website */}
          {customer.website && (
            <ContactRow
              icon={<LanguageOutlined />}
              label={translate("website")}
              value={customer.website.replace(/^https?:\/\//, "")}
              href={
                customer.website.startsWith("http")
                  ? customer.website
                  : `https://${customer.website}`
              }
              external
            />
          )}
        </Box>
      </Box>

      <KioskBackButton />
    </Box>
  );
};

/* ─────────── Contact row helper ─────────── */
interface ContactRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
  external?: boolean;
}

const ContactRow = ({ icon, label, value, href, external }: ContactRowProps) => (
  <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
    <Box sx={{ color: dt.textMuted, flexShrink: 0, mt: "2px", "& .MuiSvgIcon-root": { fontSize: "20px" } }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          fontSize: "11px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "1px",
          color: dt.textMuted,
          mb: "2px",
        }}
      >
        {label}
      </Box>
      {href ? (
        <Box
          component="a"
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: dt.textLink,
            textDecoration: "none",
            wordBreak: "break-all",
            "&:active": { opacity: 0.7 },
          }}
        >
          {value}
        </Box>
      ) : (
        <Box
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: dt.textPrimary,
            wordBreak: "break-word",
          }}
        >
          {value}
        </Box>
      )}
    </Box>
  </Box>
);

export default CompanyDetailsPage;
